import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Cable from 'actioncable'
import Modal from 'react-bootstrap-modal'
import debounce from 'lodash.debounce'

import { ReservationProductList as ProductList } from '../../containers/shop/product-list'
import Cart from '../../containers/shop/cart'
import CartSummary from '../shop/cart-summary'
import CreditCardList from '../credit-card-list'
import PaymentTypeSelector from '../payment-type-selector'
import BraintreePaymentModal from '../braintree-payment-modal'
import StripePaymentModal from '../stripe-payment-modal'
import StripeTerminalConnect from '../../components/stripe-terminal-connect'
import StripeTerminalModal from '../../components/stripe-terminal-modal'
import StripeM2Modal from '../../components/stripe-m2-modal'
import StripeM2Connect from '../../components/stripe-m2-connect'
import PlayerSelector from '../player-selector'
import FlashMessage from '../../containers/flash-message'
import OpenOrderQRCode from '../../containers/shop/open-order-qr-code'
import { calculateCart } from '../../shop/common'

export default function PaymentModal(props) {
  const { game, playerReservation, reservationPayment, shop, products } = props
  const [showManualCardPayment, setShowManualCardPayment] = useState(false)
  const [showTerminalModal, setShowTerminalModal] = useState(false)
  const [showM2Modal, setShowM2Modal] = useState(false)
  const [terminal, setTerminal] = useState(null)
  const [readers, setReaders] = useState([])
  const [connectedReader, setConnectedReader] = useState(null)
  const [stripeLoading, setStripeLoading] = useState(false)

  const cardsClassModifier = reservationPayment.isCartOpen ? 'reservation-payment__cards--shrunk' : ''
  const { amountWithoutTax, totalAmount, appFee, appFeeInPrice } = calculateCart(shop.cart, products)
  const withoutTax = ('withoutTax' in reservationPayment) ? reservationPayment.withoutTax : false
  const entity = shop.entity
  const stripe = entity.stripe
  const allowM2 = !!stripe && stripe.allow_m2 && !!window.webkit

  useEffect(() => {
    props.loadData(props.playerReservation)

    const cable = Cable.createConsumer(props.socketUrl)
    const paymentChannel = cable.subscriptions.create({
      channel: 'PaymentsChannel',
      location_id: reservationPayment.locationId
    }, {
      received: (data) => {
        handleTouchlessPaymentSuccess(data)
      }
    });
    return () => {
      cable.subscriptions.remove(paymentChannel)
    }
  }, [])

  const handleTouchlessPaymentSuccess = (data) => {
    props.onTouchlessSuccess(data)
  }

  const handlePayment = (params) => {
    setShowManualCardPayment(false)
    setShowTerminalModal(false)
    params.type === 'card_number' ? props.handleCardNumber(params) : props.submitOrder(params)
  }

  const startLoadingAndPayment = (params) => {
    props.startLoading()
    debouncedPayment(params)
  }

  const debouncedPayment = debounce(handlePayment, 300)

  const showExternalPaymentList = () => {
    const externalPaymentTypes = reservationPayment.externalPaymentTypes

    return (
      <div className='reservation-payment__top-buttons clearfix'>
        { Object.keys(externalPaymentTypes).map(type => {
           if (type == 'credit') {
             const credit_delta = playerReservation.player_credit - amountWithoutTax

             return <button
               className={`btn btn-credit-${credit_delta > -1 ? 'available' : 'unavailable'}`}
               onClick={() => !reservationPayment.isLoading && startLoadingAndPayment({type: type})}
               disabled={credit_delta <= -1}
               key={type}>
               Game Credits
             </button>
           } else return <button
             className={`btn btn-${type}`}
             onClick={() => !reservationPayment.isLoading && startLoadingAndPayment({type: type})} key={type}>
             {externalPaymentTypes[type]}
           </button>
        })}
      </div>
    )
  }

  const onClose = () => {
    props.clearData()
    props.onClose()
  }

  if (!reservationPayment.locationId) return null

  return (
    <Modal container={document.body} show={true} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FlashMessage />
        <div>
          <OpenOrderQRCode />
          {playerReservation.player_id &&
            <p>{playerReservation.player_credit ?
              `Player: ${playerReservation.displayed_name} (credit: ${playerReservation.player_credit})` :
              `Player: ${playerReservation.displayed_name}`}
            </p>
          }
          {!playerReservation.player_id &&
            <PlayerSelector onChange={props.changePlayer} reservationId={game.id}/>
          }
          {showExternalPaymentList()}
          <div className='reservation-payment__cart'>
            <div className='reservation-payment__cart-summary'>
              {!reservationPayment.isProductListExpanded &&
                <CartSummary currency={entity.currency} withoutTax={withoutTax} onClickQuestion={props.toggleCart} />
              }
            </div>
            {reservationPayment.isCartOpen && !reservationPayment.isProductListExpanded && <Cart />}
            {reservationPayment.isProductListExpanded &&
              <Cart buttonText='Add to sale' onClickButton={props.collapseProducts} />
            }
          </div>
          <div className='reservation-payment__products'>
            <ProductList
              onClickPlus={reservationPayment.isProductListExpanded ? null : props.expandProducts}
              limit={reservationPayment.isProductListExpanded ? null : 2} />
          </div>
          {!reservationPayment.isProductListExpanded &&
            <div className={`reservation-payment__cards ${cardsClassModifier}`}>
              { reservationPayment.isLoading && <div className='animated-spinner'></div> }
              <CreditCardList
                onCharge={token => !reservationPayment.isLoading && startLoadingAndPayment({type: 'saved_card', token: token})}
                items={reservationPayment.creditCardList.items}
                active={!reservationPayment.isLoading}
              />
            </div>
          }
          {!reservationPayment.isProductListExpanded &&
            <PaymentTypeSelector
              cardAmount={totalAmount}
              onManualCard={() => setShowManualCardPayment(true)}
              showTouchless={reservationPayment.showTouchless}
              showTerminal={!!stripe && !!stripe.account}
              showM2={allowM2}
              terminalLoading={stripeLoading}
              m2Connected={stripe && stripe.m2Connected}
              onTouchless={props.handleTouchless}
              onBBPOSTerminal={() => setShowTerminalModal(true)}
              onM2Terminal={() => setShowM2Modal(true)}
            />
          }
          {reservationPayment.isProductListExpanded &&
            <button className='btn btn-info' onClick={props.collapseProducts}>Back</button>
          }
          {!reservationPayment.isProductListExpanded && 
            <a className='btn btn-info' onClick={onClose}>Back</a>
          }
          { !stripe && 
            <BraintreePaymentModal isOpen={showManualCardPayment}
              showStoreCard={true}
              amount={totalAmount}
              reservationId={game.id}
              playerId={playerReservation.player_id}
              id={playerReservation.id}
              onRequestClose={() => setShowManualCardPayment(false)}
              onPaymentMethodReceived={handlePayment}
              onCardsUpdate={props.updateCards}
            />
          }
          { !!stripe && showManualCardPayment && 
            <StripePaymentModal
              currency={entity.currency}
              amount={stripe.account ? totalAmount : amountWithoutTax}
              appFee={stripe.account ? (appFee + appFeeInPrice) : 0}
              stripe={stripe}
              location={reservationPayment.locationId}
              game={game.id}
              player={playerReservation.player_id}
              storeOption
              onClose={() => setShowManualCardPayment(false)}
              onPayment={handlePayment}
              onCardsUpdate={props.updateCards}
            />
          }
          { showTerminalModal &&
            <StripeTerminalModal
              terminal={terminal}
              reader={connectedReader}
              currency={entity.currency}
              amount={totalAmount}
              appFee={appFee + appFeeInPrice}
              location={reservationPayment.locationId}
              onClose={() => setShowTerminalModal(false)}
              onPayment={handlePayment}
            />
          }
          { showM2Modal &&
            <StripeM2Modal
              connected={stripe.m2Connected}
              currency={entity.currency}
              amount={totalAmount}
              appFee={appFee + appFeeInPrice}
              location={shop.entity.locationId}
              onClose={() => setShowM2Modal(false)}
              onPayment={props.onM2Payment}
            />
          }
          { !!stripe && 
            <StripeTerminalConnect
              stripe={stripe}
              terminal={terminal}
              readers={readers}
              connectedReader={connectedReader}
              location={reservationPayment.locationId}
              onTerminalSet={(value) => setTerminal(value)}
              onReadersFetch={(value) => setReaders(value)}
              onReaderConnect={(value) => setConnectedReader(value)}
              onError={(value) => console.log('STRIPE ERROR: ', value)}
              onLoading={(value) => setStripeLoading(value)}
            />
          }
          { allowM2 &&
            <StripeM2Connect
              stripe={stripe}
              location={shop.entity.locationId}
              initConnection={props.onM2Connection}
            />
          }
        </div>
      </Modal.Body>
    </Modal>
  )
}

PaymentModal.propTypes = {
  reservationPayment: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
  expandProducts: PropTypes.func.isRequired,
  collapseProducts: PropTypes.func.isRequired,
  toggleCart: PropTypes.func.isRequired,
  changePlayer: PropTypes.func.isRequired,
  handleCardNumber: PropTypes.func.isRequired,
  updateCards: PropTypes.func.isRequired,
  submitOrder: PropTypes.func.isRequired,
  loadData: PropTypes.func.isRequired,
  clearData: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired
}
